import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Tag, Space, Input, Select, Button, Row, Col,
  Statistic, message, Tooltip, Modal, Descriptions, Typography,
  Form, InputNumber, DatePicker, Switch
} from 'antd';
import {
  TagOutlined, SearchOutlined, DownloadOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  ShopOutlined, PercentageOutlined, GiftOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';
import BackButton from '../../components/BackButton';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const AdminCoupons = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const applyToAllPartners = Form.useWatch('apply_to_all_partners', form);
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [summary, setSummary] = useState({});
  const [partners, setPartners] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState({
    partner: null,
    is_active: null,
    is_valid: null,
    discount_type: null,
    search: '',
  });

  useEffect(() => {
    fetchCoupons();
    fetchPartners();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.partner) params.partner = filters.partner;
      if (filters.is_active !== null) params.is_active = filters.is_active;
      if (filters.is_valid !== null) params.is_valid = filters.is_valid;
      if (filters.discount_type) params.discount_type = filters.discount_type;
      if (filters.search) params.search = filters.search;

      const response = await api.get('/partners/admin/coupons/', { params });
      setCoupons(response.data.coupons || response.data.results || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      message.error(t('admin.coupons.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await api.get('/partners/admin/');
      setPartners(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load partners');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchCoupons();
  };

  const clearFilters = () => {
    setFilters({
      partner: null,
      is_active: null,
      is_valid: null,
      discount_type: null,
      search: '',
    });
  };

  const showCouponDetail = (coupon) => {
    setSelectedCoupon(coupon);
    setDetailModalVisible(true);
  };

  const showCreateModal = () => {
    setEditingCoupon(null);
    form.resetFields();
    form.setFieldsValue({
      discount_type: 'PERCENTAGE',
      is_active: true,
      usage_limit: null,
      usage_limit_per_user: 1,
      min_order_amount: 0,
      apply_to_all_partners: true,
      applicable_partners_ids: [],
    });
    setEditModalVisible(true);
  };

  const showEditModal = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      ...coupon,
      validity: [dayjs(coupon.valid_from), dayjs(coupon.valid_until)],
      apply_to_all_partners: coupon.apply_to_all_partners ?? true,
      applicable_partners_ids: coupon.applicable_partners_ids || [],
    });
    setEditModalVisible(true);
  };

  const handleSaveCoupon = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        ...values,
        valid_from: values.validity[0].toISOString(),
        valid_until: values.validity[1].toISOString(),
        apply_to_all_partners: values.apply_to_all_partners ?? true,
        applicable_partners_ids: values.apply_to_all_partners ? [] : (values.applicable_partners_ids || []),
      };
      delete payload.validity;

      if (editingCoupon) {
        await api.patch(`/partners/admin/coupons/${editingCoupon.id}/`, payload);
        message.success(t('admin.coupons.updateSuccess'));
      } else {
        await api.post('/partners/admin/coupons/', payload);
        message.success(t('admin.coupons.createSuccess'));
      }

      setEditModalVisible(false);
      setEditingCoupon(null);
      form.resetFields();
      fetchCoupons();
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.coupons.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await api.delete(`/partners/admin/coupons/${couponId}/`);
      message.success(t('admin.coupons.deleteSuccess'));
      fetchCoupons();
    } catch (error) {
      message.error(t('admin.coupons.deleteFailed'));
    }
  };

  const handleExport = () => {
    const exportData = coupons.map(coupon => ({
      ...coupon,
      valid_from: dayjs(coupon.valid_from).format('DD/MM/YYYY HH:mm'),
      valid_until: dayjs(coupon.valid_until).format('DD/MM/YYYY HH:mm'),
      created_at: dayjs(coupon.created_at).format('DD/MM/YYYY HH:mm'),
    }));
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.coupons.code'), dataIndex: 'code' },
      { title: t('admin.coupons.name'), dataIndex: 'name' },
      { title: t('admin.coupons.partner'), dataIndex: 'partner_name' },
      { title: t('admin.coupons.discountType'), dataIndex: 'discount_type' },
      { title: t('admin.coupons.discountValue'), dataIndex: 'discount_value' },
      { title: t('admin.coupons.usageLimit'), dataIndex: 'usage_limit' },
      { title: t('admin.coupons.timesUsed'), dataIndex: 'times_used' },
      { title: t('admin.coupons.validFrom'), dataIndex: 'valid_from' },
      { title: t('admin.coupons.validUntil'), dataIndex: 'valid_until' },
      { title: t('admin.coupons.active'), dataIndex: 'is_active' },
      { title: t('admin.coupons.totalDiscount'), dataIndex: 'total_discount_given' },
    ];
    exportToExcel(exportData, exportColumns, 'coupons', t('admin.coupons.title'));
    message.success(t('admin.coupons.exportSuccess'));
  };

  const getStatusTag = (coupon) => {
    if (!coupon.is_active) {
      return <Tag color="default" icon={<CloseCircleOutlined />}>{t('admin.coupons.inactive')}</Tag>;
    }
    if (!coupon.is_valid) {
      return <Tag color="red" icon={<CloseCircleOutlined />}>{t('admin.coupons.expired')}</Tag>;
    }
    return <Tag color="green" icon={<CheckCircleOutlined />}>{t('admin.coupons.valid')}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: t('admin.coupons.code'),
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Text strong copyable style={{ fontFamily: 'monospace' }}>
          {code}
        </Text>
      ),
    },
    {
      title: t('admin.coupons.applicableTo'),
      key: 'applicable_partners',
      render: (_, record) => (
        <div>
          <ShopOutlined />{' '}
          {record.apply_to_all_partners ? (
            <Tag color="blue">{t('admin.coupons.allPartners')}</Tag>
          ) : record.applicable_partners_names?.length > 0 ? (
            <Tooltip title={record.applicable_partners_names.join(', ')}>
              <span>
                {record.applicable_partners_names.length === 1
                  ? record.applicable_partners_names[0]
                  : `${record.applicable_partners_names.length} ${t('admin.coupons.partners')}`}
              </span>
            </Tooltip>
          ) : (
            record.partner_name || 'Animap'
          )}
        </div>
      ),
    },
    {
      title: t('admin.coupons.discount'),
      key: 'discount',
      render: (_, record) => (
        <Space>
          {record.discount_type === 'PERCENTAGE' ? (
            <Tag color="blue" icon={<PercentageOutlined />}>
              {record.discount_value}%
            </Tag>
          ) : (
            <Tag color="green" icon={<GiftOutlined />}>
              €{record.discount_value}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('admin.coupons.usage'),
      key: 'usage',
      render: (_, record) => (
        <span>
          {record.times_used} / {record.usage_limit || '∞'}
        </span>
      ),
    },
    {
      title: t('admin.coupons.validity'),
      key: 'validity',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.valid_from).format('DD/MM/YYYY')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.valid_until).format('DD/MM/YYYY')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('admin.coupons.status'),
      key: 'status',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: t('admin.coupons.totalGiven'),
      dataIndex: 'total_discount_given',
      key: 'total_discount_given',
      render: (val) => `€${(val || 0).toFixed(2)}`,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showCouponDetail(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: t('admin.coupons.deleteConfirm'),
                  onOk: () => handleDeleteCoupon(record.id),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('admin.coupons.totalCoupons')}
              value={summary.total || 0}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('admin.coupons.activeCoupons')}
              value={summary.active || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('admin.coupons.expiredCoupons')}
              value={summary.expired || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('admin.coupons.totalUsage')}
              value={summary.total_uses || 0}
              prefix={<GiftOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <TagOutlined />
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('admin.coupons.title')}
            </span>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder={t('admin.coupons.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.coupons.filterByPartner')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filters.partner}
              onChange={(value) => handleFilterChange('partner', value)}
            >
              {partners.map(p => (
                <Select.Option key={p.id} value={p.id}>
                  {p.store_name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('admin.coupons.filterByStatus')}
              style={{ width: '100%' }}
              allowClear
              value={filters.is_valid}
              onChange={(value) => handleFilterChange('is_valid', value)}
            >
              <Select.Option value="true">{t('admin.coupons.valid')}</Select.Option>
              <Select.Option value="false">{t('admin.coupons.invalid')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('admin.coupons.filterByType')}
              style={{ width: '100%' }}
              allowClear
              value={filters.discount_type}
              onChange={(value) => handleFilterChange('discount_type', value)}
            >
              <Select.Option value="PERCENTAGE">{t('admin.coupons.percentage')}</Select.Option>
              <Select.Option value="FIXED">{t('admin.coupons.fixed')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <Space>
              <Button type="primary" onClick={applyFilters}>
                {t('admin.applyFilters')}
              </Button>
              <Button onClick={clearFilters}>
                {t('admin.clearFilters')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={!coupons.length}
              >
                {t('admin.exportExcel')}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={showCreateModal}
              >
                {t('admin.coupons.create')}
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={coupons}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`${t('admin.coupons.couponDetails')} - ${selectedCoupon?.code}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCoupon(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>
        ]}
        width={600}
      >
        {selectedCoupon && (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label={t('admin.coupons.code')} span={2}>
              <Text strong copyable style={{ fontFamily: 'monospace' }}>
                {selectedCoupon.code}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.name')} span={2}>
              {selectedCoupon.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.applicableTo')}>
              {selectedCoupon.apply_to_all_partners ? (
                <Tag color="blue">{t('admin.coupons.allPartners')}</Tag>
              ) : selectedCoupon.applicable_partners_names?.length > 0 ? (
                selectedCoupon.applicable_partners_names.map((name, idx) => (
                  <Tag key={idx}>{name}</Tag>
                ))
              ) : (
                selectedCoupon.partner_name || 'Animap'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.createdBy')}>
              {selectedCoupon.created_by}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.discountType')}>
              {selectedCoupon.discount_type === 'PERCENTAGE' ? t('admin.coupons.percentage') : t('admin.coupons.fixed')}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.discountValue')}>
              {selectedCoupon.discount_type === 'PERCENTAGE'
                ? `${selectedCoupon.discount_value}%`
                : `€${selectedCoupon.discount_value}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.minOrder')}>
              €{selectedCoupon.min_order_amount}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.maxDiscount')}>
              {selectedCoupon.max_discount_amount ? `€${selectedCoupon.max_discount_amount}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.usageLimit')}>
              {selectedCoupon.usage_limit || t('admin.coupons.unlimited')}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.timesUsed')}>
              {selectedCoupon.times_used}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.validFrom')}>
              {dayjs(selectedCoupon.valid_from).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.validUntil')}>
              {dayjs(selectedCoupon.valid_until).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.status')}>
              {getStatusTag(selectedCoupon)}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.coupons.totalGiven')}>
              €{(selectedCoupon.total_discount_given || 0).toFixed(2)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        title={editingCoupon ? t('admin.coupons.editCoupon') : t('admin.coupons.createCoupon')}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingCoupon(null);
          form.resetFields();
        }}
        onOk={handleSaveCoupon}
        confirmLoading={saving}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label={t('admin.coupons.code')}
                rules={[{ required: true, message: t('admin.coupons.codeRequired') }]}
              >
                <Input placeholder="SAVE20" style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('admin.coupons.name')}
              >
                <Input placeholder="Summer Sale 20%" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="apply_to_all_partners"
                label={t('admin.coupons.applyToAllPartners')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discount_type"
                label={t('admin.coupons.discountType')}
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="PERCENTAGE">{t('admin.coupons.percentage')}</Select.Option>
                  <Select.Option value="FIXED">{t('admin.coupons.fixed')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {!applyToAllPartners && (
            <Form.Item
              name="applicable_partners_ids"
              label={t('admin.coupons.selectPartners')}
              rules={[{ required: !applyToAllPartners, message: t('admin.coupons.selectPartnersRequired') }]}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder={t('admin.coupons.selectPartnersPlaceholder')}
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {partners.map(p => (
                  <Select.Option key={p.id} value={p.id}>
                    {p.store_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="discount_value"
                label={t('admin.coupons.discountValue')}
                rules={[{ required: true, message: t('admin.coupons.discountRequired') }]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="min_order_amount"
                label={t('admin.coupons.minOrder')}
              >
                <InputNumber min={0} prefix="€" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="max_discount_amount"
                label={t('admin.coupons.maxDiscount')}
              >
                <InputNumber min={0} prefix="€" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="validity"
                label={t('admin.coupons.validityPeriod')}
                rules={[{ required: true, message: t('admin.coupons.validityRequired') }]}
              >
                <RangePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="usage_limit"
                label={t('admin.coupons.usageLimit')}
              >
                <InputNumber min={1} placeholder="∞" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="usage_limit_per_user"
                label={t('admin.coupons.perUserLimit')}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('admin.coupons.description')}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('admin.coupons.active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminCoupons;
